// Deliberate-motion utilities — a staggered entrance wrapper + a sub-400ms
// integer count-up hook. Both are prefers-reduced-motion safe (and degrade to
// instant when rAF is unavailable, e.g. in jsdom tests).

import { useEffect, useRef, useState } from 'react';
import './redesign.css';

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function StaggerReveal({ as: Tag = 'div', className = '', children, ...rest }) {
  return (
    <Tag className={`stagger-reveal ${className}`.trim()} {...rest}>
      {children}
    </Tag>
  );
}

export function useCountUp(target, { duration = 360 } = {}) {
  const numericTarget = typeof target === 'number' && Number.isFinite(target) ? target : null;
  const instant =
    numericTarget == null || prefersReducedMotion() || typeof requestAnimationFrame === 'undefined';
  const [val, setVal] = useState(() => (instant ? target : 0));
  const raf = useRef(0);

  useEffect(() => {
    if (instant) {
      setVal(target);
      return undefined;
    }
    let start = null;
    const from = 0;
    const tick = (ts) => {
      if (start == null) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + (numericTarget - from) * eased));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, numericTarget, duration, instant]);

  return val;
}
